/**
 * loginPromptOverlayUi: ログイン導線URLの組み立てユーティリティ。
 *
 * <p><b>2026-08-25変更</b>: 従来ここに実装していた`LoginPromptOverlay`（右下固定の別ポップアップ、
 * `promptVisitorLogin`/`showLoginPromptOverlay`）は削除した。ウィジェット本体（SurveyPopup・
 * PreBookingForm・MultiCvAppointmentList）は2026-08-19の変更で`WidgetPanel`のログインゲート
 * （パネル内モザイク＋重畳カード方式）に統一済みだったが、最後に残っていたトップページ常時埋め込みの
 * `AppointmentCancelView`（キャンセルモーダル）経路も同方式（`createWidgetPanel`の`showLoginGate`）へ
 * 統一したため、`LoginPromptOverlay`自体の呼び出し元がなくなった。
 * バグ修正の詳細: `aidlc-docs/construction/plans/u6-cancel-modal-toppage-embed-bugfix-plan.md`。
 * 本ファイルは`buildLoginRedirectUrl`（`widgetPanelUi.ts`の`buildWidgetLoginUrl`が再利用）のみを残す。
 */

import type { LoginPromptReason } from "../types";

/**
 * ログイン後にcontinueToUrlとして現在のページへ戻るリダイレクトURLを組み立てる。
 * 実際のログインエンドポイント（ホスト側）は中継エンドポイント契約側で確定する。
 *
 * <p><b>2026-08-19バグ修正</b>: クエリパラメータ名を`continueTo`から`continue`に修正した。
 * ホスト（polaris-mono）側の既存ログイン導線（`ajax-mailmagazine-information.js`等）は
 * `continue`という名前でパラメータを渡しており、ホスト側の`AbstractAuthenticationService`が
 * `continueToUrl`を解決する際もこの名前を前提にしている。従来の`continueTo`はホスト側に
 * 認識されず、ログイン後の戻り先が常にサイトトップへフォールバックしていた
 * （`doAuthentication`は`continueToUrl`未設定時にトップへフォールバックする実装）。
 *
 * <p><b>2026-09-04バグ修正（特設サイト対応）</b>: ログイン先ホストを、特設サイトの既存ログイン導線と
 * 同じく「先頭 `pr.` を除いたサービスサイトホスト」の絶対URL `/login` へ向けるよう変更した。
 * 特設サイト（`pr.<host>`）で相対パス `/login` を踏むと `https://pr.<host>/login`（`pr.` が付いたまま）
 * となり404になっていた。既存の特設サイトはログイン時に `pr.` を除いた `https://<host>/login` を叩いており
 * （中継API `createApiUrl`＝`https://${location.host.replace(/^pr\./,"")}/api...` と同じ `pr.` 除去方針）、
 * widgetのログイン導線もこれに合わせて `pr.` を除いたホストの絶対URL `/login` へ向けることでログインが成立する。
 * ログイン後の戻り先（`continue`）は現在ページ（特設サイト、`pr.` 付き）のURLを維持する。
 */
export function buildLoginRedirectUrl(currentUrl: string, reason: LoginPromptReason): string {
  const params = new URLSearchParams({ continue: currentUrl, reason });
  return `${serviceSiteHostBase()}/login?${params.toString()}`;
}

/** サービスサイトのホストの先頭ラベル（mono＝ものづくり、www＝イプロス）。会員サイトのホストへ置き換える対象。 */
const SERVICE_SITE_FIRST_LABELS: ReadonlySet<string> = new Set(["mono", "www"]);

/**
 * 新規会員登録画面のURLを組み立てる（I261-77 仕様変更: ログイン／新規会員登録のボタン分離）。
 *
 * <p>サービスサイトの既存の「新規会員登録」リンク（HeartCore: `{path_my}/preRegisterMail?continue={path_site}`）と
 * 同じく、会員（my）サイトの `/preRegisterMail` へ、登録後の戻り先 `continue` を付けて遷移させる。
 * `continue` はログイン導線と同じく現在ページのURL（特設サイトでは `pr.` 付きのまま）を渡す。
 * `reason` もログイン導線と同じ値を付ける（どの操作の途中で新規会員登録へ進んだかを、アクセスログで追えるようにするため。
 * ホスト側の認証フィルタが読むのは `continue` だけで、`reason` は無視される）。
 */
export function buildRegisterRedirectUrl(currentUrl: string, reason: LoginPromptReason): string {
  const params = new URLSearchParams({ continue: currentUrl, reason });
  return `${mySiteHostBase()}/preRegisterMail?${params.toString()}`;
}

/**
 * 会員（my）サイトのホスト部（`https://my.<環境ドメイン>`）を返す。
 *
 * <p>サービスサイト（mono／machi）のホスト先頭ラベル（`pr.` を除いたあとの `mono` / `www`）を `my` に置き換える。
 * 会員サイトは mono／machi 共通で、環境ごとに次のとおり対応する（polaris-mono／polaris-machi の
 * `application-*.yml` の `my:` と同じ）。
 * <ul>
 * <li>本番: `mono.ipros.com` / `www.ipros.com` → `my.ipros.com`</li>
 * <li>stg: `mono.stg1.ipros.com` / `www.stg1.ipros.com` → `my.stg1.ipros.com`</li>
 * <li>dev: `mono.dev1.ipros-dev.com` / `www.dev1.ipros-dev.com` → `my.dev1.ipros-dev.com`</li>
 * </ul>
 * spica が動く環境は dev／stg／本番のみ。HeartCore 開発用の `*-hcdev` ホストは対象外。
 * 先頭ラベルが `mono`／`www` 以外のホスト（`ipros.com`、IP、localhost 等）は置き換えない。
 * `location` が利用できない場合は空文字（相対パス）にフォールバックする。
 */
export function mySiteHostBase(): string {
  if (typeof location === "undefined" || !location.host) {
    return "";
  }
  return deriveMySiteHostBase(location.host, location.protocol);
}

/**
 * {@link mySiteHostBase} の本体（`location` に依存しない純粋関数）。
 *
 * @param host `location.host` 相当（ポート付きでもよい）
 * @param protocol `location.protocol` 相当（例: `https:`）
 */
export function deriveMySiteHostBase(host: string, protocol: string): string {
  const siteHost = host.replace(/^pr\./, "");
  const labels = siteHost.split(".");
  // サービスサイト（先頭ラベルが mono／www）のときだけ、先頭ラベルを my に置き換える。
  // それ以外（apex の ipros.com、IP アドレス、localhost、*-hcdev など）は存在しない my ホストへ飛ばさないよう、
  // 置き換えずそのままのホストを返す。
  const myHost = labels.length >= 3 && SERVICE_SITE_FIRST_LABELS.has(labels[0]) ? ["my", ...labels.slice(1)].join(".") : siteHost;
  return `${protocol}//${myHost}`;
}

/**
 * サービスサイトのホスト部（`https://<pr.を除いたホスト>`）を返す共通ユーティリティ。
 *
 * <p>特設サイト（`pr.<host>`）では、Apacheが全パスへ `/pr` を前置してTomcatへプロキシするため、
 * `pr.` 付きホストの相対パス・絶対URLはサービスサイトのパスに解決されず404になる。そのため
 * ログイン導線（`buildLoginRedirectUrl`）や重複アポイント時のキャンセル導線
 * （`bookingSectionUi.showDuplicateCompanyAppointmentError`）など、サービスサイト側の画面へ
 * 遷移させたいリンクは、このユーティリティで先頭 `pr.` を除いた絶対URLに揃える。
 * 中継API（`relayClient.toApiUrl`）とホスト解決方針を揃えている。
 * ものづくり（`pr.` が付かない通常ホスト）ではホストがそのまま返る。
 * `location` が利用できない場合は空文字（相対パス）にフォールバックする。
 */
export function serviceSiteHostBase(): string {
  if (typeof location === "undefined" || !location.host) {
    return "";
  }
  const host = location.host.replace(/^pr\./, "");
  return `${location.protocol}//${host}`;
}
